import { Input, Radio } from 'antd';
import LabelRequired from '../../Common/LabelRequired';
import { getSessionData } from '../../../utils/storage';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { recorbAppDownloadSchema } from '../../../validation/extension.schema';
import { forwardRef, useImperativeHandle } from 'react';

interface RecorbBoxDownloadProps {
  serialType: 'camera' | 'hardware';
  serialNumber: string;
  onSerialTypeChange: (value: 'camera' | 'hardware') => void;
  onSerialNumberChange: (value: string) => void;
}

export interface RecorbBoxDownloadRef {
  validate: () => Promise<boolean>;
}

const RecorbBoxDownload = forwardRef<RecorbBoxDownloadRef, RecorbBoxDownloadProps>(
  ({ serialType, serialNumber, onSerialTypeChange, onSerialNumberChange }, ref) => {
    const sessionData = getSessionData();

    const {
      control,
      formState: { errors },
      trigger,
    } = useForm({
      resolver: yupResolver(recorbAppDownloadSchema),
      mode: 'onChange',
      defaultValues: {
        serialNumber: serialNumber,
        agentName: sessionData.agentName || 'Agent name',
        agencyContactPerson: sessionData.agentPersonInCharge || 'Agency name',
        agencyEmail: sessionData.agencyEmail || 'agencyemail@dipro.vn',
      },
    });

    useImperativeHandle(ref, () => ({
      validate: async () => {
        return await trigger();
      },
    }));

    return (
      <div className='p-6 bg-[#F4F6F9] border border-[#BCC2FF] rounded-2xl flex gap-6'>
        <div className='w-1/2 mt-[31px]'>
          <Radio.Group
            className='flex flex-col gap-2'
            value={serialType}
            onChange={(e) => onSerialTypeChange(e.target.value)}
          >
            <Radio
              value='hardware'
              className={`text-lg text-[#2C3A4B] ${
                serialType === 'hardware' ? 'font-bold' : 'font-medium'
              }`}
            >
              Hardware Serial Number
            </Radio>
            <Radio
              value='camera'
              className={`text-lg text-[#2C3A4B] ${
                serialType === 'camera' ? 'font-bold' : 'font-medium'
              }`}
            >
              Camera Serial Number
            </Radio>
          </Radio.Group>

          <Controller
            name='serialNumber'
            control={control}
            render={({ field }) => (
              <div>
                <Input
                  {...field}
                  type='text'
                  placeholder='Enter serial number'
                  className='mt-4 w-full !h-[46px]'
                  status={errors.serialNumber ? 'error' : ''}
                  onChange={(e) => {
                    field.onChange(e);
                    onSerialNumberChange(e.target.value);
                  }}
                />
                {errors.serialNumber && (
                  <p className='mt-1 text-sm text-red-500'>{errors.serialNumber.message}</p>
                )}
              </div>
            )}
          />
        </div>
        <div className='flex flex-col gap-4 w-1/2 mt-[75px]'>
          <div>
            <LabelRequired text='Agent name' />
            <Controller
              name='agentName'
              control={control}
              render={({ field }) => (
                <div>
                  <Input
                    {...field}
                    type='text'
                    disabled
                    className='w-full h-[46px]'
                    status={errors.agentName ? 'error' : ''}
                  />
                  {errors.agentName && (
                    <p className='mt-1 text-sm text-red-500'>{errors.agentName.message}</p>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <LabelRequired text='Name of person in charge of the agency' />
            <Controller
              name='agencyContactPerson'
              control={control}
              render={({ field }) => (
                <div>
                  <Input
                    {...field}
                    type='text'
                    disabled
                    className='w-full h-[46px]'
                    status={errors.agencyContactPerson ? 'error' : ''}
                  />
                  {errors.agencyContactPerson && (
                    <p className='mt-1 text-sm text-red-500'>
                      {errors.agencyContactPerson.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <LabelRequired text='Agency contact email address' />
            <Controller
              name='agencyEmail'
              control={control}
              render={({ field }) => (
                <div>
                  <Input
                    {...field}
                    type='text'
                    disabled
                    className='w-full h-[46px]'
                    status={errors.agencyEmail ? 'error' : ''}
                  />
                  {errors.agencyEmail && (
                    <p className='mt-1 text-sm text-red-500'>{errors.agencyEmail.message}</p>
                  )}
                </div>
              )}
            />
          </div>
        </div>
      </div>
    );
  }
);

export default RecorbBoxDownload;
